import { Suspense, lazy, useCallback, useEffect, useMemo, useState } from "react";
import {
  fetchConfig,
  getFile,
  listFiles,
  type ContentKind,
  type Direction,
  type FileDetail as FileDetailData,
  type FileSummary,
  type InspectorConfig,
  type Mailbox,
} from "./api";
import { SignIn } from "./auth/SignIn";
import { configureAuth, currentToken, currentViewer, renewToken, signOut } from "./auth/session";
import { AdminBoard } from "./components/AdminBoard";
import { DeliveriesBoard } from "./components/DeliveriesBoard";
import { FileRail } from "./components/FileRail";

// Redoc is most of the bundle; it is fetched the first time the reference is
// opened, not on every visit to the file list.
const ApiReference = lazy(() => import("./docs/ApiReference").then((module) => ({ default: module.ApiReference })));
import { FileDetail } from "./components/FileDetail";
import { Documentation, type SectionRequest } from "./docs/Documentation";

interface Session {
  token: string;
  viewer: string;
  signOut: () => void;
}

// Local development has no user pool to sign in against. The bypass is keyed on
// the SERVER reporting no pool — which a deployed environment never does, since
// the stack always sets one — and it grants nothing: every request still goes
// through the API Gateway JWT authorizer, which is where access is actually
// enforced. It only skips a sign-in screen that would have nothing to sign in to.
const isUnguarded = (config: InspectorConfig): boolean => config.userPoolId === "";

const LOCAL_SESSION: Session = {
  token: "",
  viewer: "local development",
  signOut: () => window.location.reload(),
};

type Boot =
  | { state: "loading" }
  | { state: "failed"; error: string }
  | { state: "ready"; config: InspectorConfig };

export function App(): JSX.Element {
  const [boot, setBoot] = useState<Boot>({ state: "loading" });
  const [session, setSession] = useState<Session | undefined>();
  // Set when a session ran out under an open page, so the sign-in screen can say why it is back.
  const [ended, setEnded] = useState(false);

  // One startup read: the pool to sign in against, and whether a session is
  // already open (Amplify keeps the tokens, so a reload does not sign you out).
  useEffect(() => {
    let cancelled = false;
    fetchConfig()
      .then(async (config) => {
        if (cancelled) return;
        if (isUnguarded(config)) {
          setSession(LOCAL_SESSION);
        } else {
          configureAuth(config);
          const existing = await openSession();
          if (existing && !cancelled) setSession(existing);
        }
        if (!cancelled) setBoot({ state: "ready", config });
      })
      .catch((err: unknown) => {
        if (!cancelled) setBoot({ state: "failed", error: message(err) });
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const signedIn = useCallback(() => {
    setEnded(false);
    void openSession().then((next) => setSession(next));
  }, []);

  // Keep the token fresh for as long as the page is open: every minute, when
  // the page comes back into view, and at once when a request was refused.
  // When the session is over, say so — never leave a page quietly failing.
  const renew = useCallback(() => {
    void renewToken().then((renewal) => {
      if (renewal.kind === "signed_out") {
        setSession(undefined);
        setEnded(true);
      } else if (renewal.kind === "ok") {
        setSession((current) => (current && current.token !== renewal.token ? { ...current, token: renewal.token } : current));
      }
      // Unreachable: keep the token we have; the next try may get through.
    });
  }, []);
  const guarded = boot.state === "ready" && !isUnguarded(boot.config) && session !== undefined;
  useEffect(() => {
    if (!guarded) return;
    const timer = window.setInterval(renew, 60_000);
    const onVisible = (): void => {
      if (document.visibilityState === "visible") renew();
    };
    document.addEventListener("visibilitychange", onVisible);
    return () => {
      window.clearInterval(timer);
      document.removeEventListener("visibilitychange", onVisible);
    };
  }, [guarded, renew]);

  if (boot.state === "loading") return <Splash body="Loading…" />;
  if (boot.state === "failed") {
    return (
      <Splash
        body={`The inspector could not be reached: ${boot.error}`}
        action={{ label: "Reload", onClick: () => window.location.reload() }}
      />
    );
  }
  if (!session) {
    return (
      <SignIn
        config={boot.config}
        onSignedIn={signedIn}
        notice={ended ? "Your session has ended (they last twelve hours). Sign in again to carry on — the page stops updating until you do." : undefined}
      />
    );
  }
  return <Inspector config={boot.config} session={session} onUnauthorized={renew} />;
}

/** Reads the current tokens into a session, or undefined when signed out. */
async function openSession(): Promise<Session | undefined> {
  const token = await currentToken();
  if (!token) return undefined;
  return {
    token,
    viewer: (await currentViewer()) ?? "signed in",
    signOut: () => {
      void signOut().then(() => window.location.reload());
    },
  };
}

function Splash({ body, action }: { body: string; action?: { label: string; onClick: () => void } }): JSX.Element {
  return (
    <div className="gate">
      <div className="gate-card">
        <div className="gate-brand">
          <span className="gate-mark">
            Betalings<span>service</span>
          </span>
        </div>
        <h1>Files</h1>
        <p>{body}</p>
        {action ? (
          <button type="button" className="button" onClick={action.onClick}>
            {action.label}
          </button>
        ) : null}
      </div>
    </div>
  );
}

// The two halves of the interface: the files themselves, and how the service
// that produced them works. They are one application because the questions are
// one question — "what is a 0602, and what did this one say?". The API
// reference is the contract itself, for whoever is writing a caller; the admin
// board is the small fourth: the one thing you can DO here, fetch the files now.
// The deliveries board answers the operator's standing question — did the bank
// get, and accept, what we sent? — and is made to be left open.
type View = "files" | "deliveries" | "docs" | "api" | "admin";
const VIEWS: readonly View[] = ["files", "deliveries", "docs", "api", "admin"];

// The view is kept in the address (`#deliveries`), so a board left open on a
// monitor comes back as itself after a reload or a sign-in.
const viewFromHash = (): View => VIEWS.find((view) => window.location.hash === `#${view}`) ?? "files";

function Inspector({ config, session, onUnauthorized }: { config: InspectorConfig; session: Session; onUnauthorized: () => void }): JSX.Element {
  const [view, setView] = useState<View>(viewFromHash);
  useEffect(() => {
    window.history.replaceState(null, "", view === "files" ? window.location.pathname : `#${view}`);
  }, [view]);
  const [docsAt, setDocsAt] = useState<SectionRequest | undefined>();
  // Which login's files. The first enabled mailbox is the one to open on — in
  // production that is BS2, the one the service has always carried.
  const [mailbox, setMailbox] = useState<Mailbox>(config.mailboxes[0]?.id ?? "bs2");
  const [direction, setDirection] = useState<Direction>("inbound");
  const [kind, setKind] = useState<ContentKind | undefined>();
  const [query, setQuery] = useState("");
  const [files, setFiles] = useState<FileSummary[]>([]);
  const [listState, setListState] = useState<{ loading: boolean; error?: string; truncated: boolean }>({
    loading: true,
    truncated: false,
  });
  const [selected, setSelected] = useState<string | undefined>();
  const [file, setFile] = useState<FileDetailData | undefined>();
  const [fileError, setFileError] = useState<string | undefined>();
  // Bumped to list again without anything else changing — after a poll has
  // just put new files in the bucket.
  const [relist, setRelist] = useState(0);

  const debounced = useDebounced(query, 200);
  const { token } = session;

  useEffect(() => {
    let cancelled = false;
    setListState((state) => ({ ...state, loading: true, error: undefined }));
    listFiles(token, mailbox, direction, { q: debounced || undefined, limit: 200 })
      .then((page) => {
        if (cancelled) return;
        setFiles(page.files);
        setListState({ loading: false, truncated: page.truncated });
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        setFiles([]);
        setListState({ loading: false, truncated: false, error: message(err) });
      });
    return () => {
      cancelled = true;
    };
  }, [token, mailbox, direction, debounced, relist]);

  useEffect(() => {
    if (selected === undefined) {
      setFile(undefined);
      return;
    }
    let cancelled = false;
    setFileError(undefined);
    getFile(token, mailbox, direction, selected)
      .then((detail) => {
        if (!cancelled) setFile(detail);
      })
      .catch((err: unknown) => {
        if (!cancelled) {
          setFile(undefined);
          setFileError(message(err));
        }
      });
    return () => {
      cancelled = true;
    };
  }, [token, mailbox, direction, selected]);

  const changeDirection = useCallback((next: Direction) => {
    setDirection(next);
    setSelected(undefined);
    setKind(undefined);
  }, []);

  const changeMailbox = useCallback((next: Mailbox) => {
    setMailbox(next);
    setSelected(undefined);
    setKind(undefined);
  }, []);

  // "12 in the mailbox →" on the documentation page lands here: the same listing,
  // narrowed to one kind of file. The narrowing is done in the browser over the
  // page already fetched, so the count and the list are always the same number.
  // The masthead's shortcut straight to a section of the documentation.
  const openDocsAt = useCallback((section: SectionRequest["section"]) => {
    setDocsAt((previous) => ({ section, nonce: (previous?.nonce ?? 0) + 1 }));
    setView("docs");
  }, []);

  const openFiles = useCallback((nextMailbox: Mailbox, next: Direction, contentKind: ContentKind) => {
    setMailbox(nextMailbox);
    setDirection(next);
    setSelected(undefined);
    setQuery("");
    setKind(contentKind);
    setView("files");
  }, []);

  // The admin board's "show received files": the login just polled, received,
  // listed afresh.
  const showReceived = useCallback((nextMailbox: Mailbox) => {
    setMailbox(nextMailbox);
    setDirection("inbound");
    setSelected(undefined);
    setQuery("");
    setKind(undefined);
    setRelist((n) => n + 1);
    setView("files");
  }, []);

  // The deliveries board's T / V / delivery links: that one file, opened.
  const openFile = useCallback((nextMailbox: Mailbox, next: Direction, name: string) => {
    setMailbox(nextMailbox);
    setDirection(next);
    setQuery("");
    setKind(undefined);
    setSelected(name);
    setView("files");
  }, []);

  const visible = useMemo(
    () => (kind === undefined ? files : files.filter((file) => file.contentKind === kind)),
    [files, kind],
  );

  const status = useMemo(() => {
    if (listState.loading) return "Loading…";
    if (listState.error) return listState.error;
    if (kind !== undefined) return `${visible.length} of ${files.length} listed`;
    const count = `${files.length} file${files.length === 1 ? "" : "s"}`;
    return listState.truncated ? `${count} · older files not listed` : count;
  }, [files.length, visible.length, kind, listState]);

  return (
    <div className="app" data-view={view}>
      <header className="masthead">
        <h1>
          Betalings<span>service</span>
        </h1>
        <nav className="masthead-nav" aria-label="Sections">
          <button type="button" aria-pressed={view === "files"} onClick={() => setView("files")}>
            Files
          </button>
          <button type="button" aria-pressed={view === "deliveries"} onClick={() => setView("deliveries")}>
            Deliveries
          </button>
          {/* Asks for the first section by name, so it opens at the top whether
              or not the page is already showing — and after the reader has been
              sent to a section from the entry beside it. */}
          <button type="button" aria-pressed={view === "docs"} onClick={() => openDocsAt("overview")}>
            How it works
          </button>
          {/* A shortcut, not a third view — the one page people come back for. */}
          <button type="button" onClick={() => openDocsAt("dates")}>
            Dates and deadlines
          </button>
          <button type="button" aria-pressed={view === "api"} onClick={() => setView("api")}>
            API reference
          </button>
          <button type="button" aria-pressed={view === "admin"} onClick={() => setView("admin")}>
            Admin
          </button>
        </nav>
        <span className="stage-chip" data-stage={config.stage}>
          {config.stage}
        </span>
        <span className="spacer" />
        <span className="viewer">
          {session.viewer}
          <button type="button" className="linkish" onClick={session.signOut}>
            Sign out
          </button>
        </span>
      </header>

      {view === "deliveries" ? (
        <DeliveriesBoard
          token={token}
          mailboxes={config.mailboxes}
          mailbox={mailbox}
          onMailbox={changeMailbox}
          onOpenFile={openFile}
          onUnauthorized={onUnauthorized}
        />
      ) : view === "docs" ? (
        <Documentation token={token} mailboxes={config.mailboxes} onOpenFiles={openFiles} onOpenReference={() => setView("api")} open={docsAt} />
      ) : view === "api" ? (
        <Suspense fallback={<p className="reference-loading">Loading the reference…</p>}>
          <ApiReference />
        </Suspense>
      ) : view === "admin" ? (
        <AdminBoard token={token} viewer={session.viewer} mailboxes={config.mailboxes} onShowReceived={showReceived} />
      ) : (
        <div className="workspace">
          <FileRail
            mailboxes={config.mailboxes}
            mailbox={mailbox}
            onMailbox={changeMailbox}
            direction={direction}
            onDirection={changeDirection}
            query={query}
            onQuery={setQuery}
            files={visible}
            kind={kind}
            onClearKind={() => setKind(undefined)}
            selected={selected}
            onSelect={setSelected}
            status={status}
          />

          {file ? (
            <FileDetail file={file} token={token} mailboxes={config.mailboxes} />
          ) : (
            <section className="detail">
              <div className="empty">
                {fileError ? (
                  <>
                    <h2>That file could not be read</h2>
                    <p className="error">{fileError}</p>
                  </>
                ) : visible.length === 0 && !listState.loading ? (
                  <>
                    <h2>Nothing here</h2>
                    <p>
                      {kind !== undefined
                        ? "No file of that kind is in this listing."
                        : query
                          ? "No file name matches that filter."
                          : direction === "inbound"
                            ? "No files have been received from the bank on this login yet."
                            : "No deliveries have been sent to the bank on this login yet."}
                    </p>
                  </>
                ) : (
                  <>
                    <h2>Pick a file</h2>
                    <p>Every file the bank sent us, and every delivery we sent it — raw, decoded and parsed.</p>
                  </>
                )}
              </div>
            </section>
          )}
        </div>
      )}
    </div>
  );
}

const message = (err: unknown): string => (err instanceof Error ? err.message : String(err));

function useDebounced(value: string, delay: number): string {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const timer = window.setTimeout(() => setDebounced(value), delay);
    return () => window.clearTimeout(timer);
  }, [value, delay]);
  return debounced;
}
